import _ from "lodash"
import { CloudGpxSortDirections } from "./CloudGpxSortDirection"
import CloudGpxTypes from "./CloudGpxType"
import { ColourValues, IconColours } from "./Colours"
import DistanceUnits from "./DistanceUnits"
import HomeData, { AllTracksInOneGpx, CurrentTrack } from "./HomeData"
import store from "./store"
import TrackVectorLayer from "./TrackVectorLayer"
import { TrackVectorLayerType } from "./TrackVectorLayerType"
import { WaypointIconSet } from "./WaypointIconSet"
import { TrackEntity } from "./TrackEntity"
import { mdiStarThreePointsOutline } from "@mdi/js"

export default class HomeComputed extends HomeData {
    getCurrentTrack(): TrackEntity|undefined { // should be a property really, but have moved it from HometMethods.ts to here, so we can use it here...
        return this.tracks.find(t => t.value === this.currentTrack)
    }
    
    get canUndo() {
        return this.undoCommands.length > 0
    }

    get currentTrackColour() {
        return this.tracks.find(t => t.value === this.currentTrack)?.colour ?? Object.keys(ColourValues)[0]
    }
    set currentTrackColour(colour: string) {
        const track = this.tracks.find(t => t.value === this.currentTrack)
        if(track) {
            track.colour = colour
        }
    }

    get anyTracksHaveMoreThan10000Points() {
        return this.tracks.some(t => t.hasMoreThan10000Points);
    }

    get overTrackpointLimitWarning() {
        if (this.downloadOption === CurrentTrack) {
            const currentTrackEntity = this.getCurrentTrack();
            return currentTrackEntity && currentTrackEntity.hasMoreThan10000Points ? 
                `${currentTrackEntity.name} has more than 10,000 points, which may present an issue to some devices`
                : null;
        } else {
            return this.anyTracksHaveMoreThan10000Points ? 
                'At least one track has more than 10,000 points, which may present an issue to some devices'
                : null;
        }
    }

    get currentTrackName() {
        return this.tracks.find(t => t.value === this.currentTrack)?.name ?? ''
    }
    set currentTrackName (newValue: string) {
        const track = this.tracks.find(t => t.value === this.currentTrack)
        if (track && newValue !== undefined) {
            track.name = newValue
        }
    }

    get distanceUnitDescription() {
        const distanceUnitData = _.find(DistanceUnits, d => d.value === this.distanceUnit)
        if(distanceUnitData) {
            return distanceUnitData.distanceDescription
        } else {
            console.error('No distance unit data for ', this.distanceUnit)
            return 'metres'
        }
    }

    get currentTrackType() {
        return this.tracks.find(t => t.value === this.currentTrack)?.type ?? ''
    }
    set currentTrackType(newVal: string) {
        const track = this.tracks.find(t => t.value === this.currentTrack)
        if(track && track.type !== newVal) {
            track.type = newVal
            this.currentRenderer.map.getLayers().forEach((layer, layerIndex) => {
                if(layer instanceof TrackVectorLayer && layer.track === track) {
                    if(layer.type === TrackVectorLayerType.Track) {
                        layer.setStyle(track.GetStyle())
                    }
                }
            })
        }
    }
    get currentTrackDistance() {
        const currentTrack = this.tracks.find(t => t.value === this.currentTrack)
        if(currentTrack) {
            return currentTrack.totalDistance
        }
        return null
    }
    get allTracksDistance() {
        return _.sum(this.tracks.map(t => t.totalDistance))
    }
    get multipleTracksWithSegments() {
        return this.tracks.filter(t => t.getSegments().length > 0).length > 1
    }
    get cloudGpxTypes() {
        return CloudGpxTypes
    }
    get lastVisibleOperationIndex() {
        return this.operations.filter(o => !!o.icon).length - 1
    }
    get splittingTrack() {
        const op = this.operations[this.operation];
        return op && op.name === 'split'
    }
    get manageSubscriptionUrl() {
        return this.fullAccessProduct?.manageSubscriptionUrl
    }
    get navDrawIsStateless() {
        return this.cloudsidebar // if you resize from small to large when the cloud sidebar is open, the main nav bar reappears, as it becomes permanent, which can result in the hamburger icon getting hidden
            // OR this with anything else that should prevent it shutting (or reopening when resizing the window).
    }
    get snackbarLines() {
        return this.snackbarText.split('\n')
    }
    get colours() {
        return Object.keys(ColourValues)
    }
    get iconColours() {
        return Object.keys(IconColours)
    }
    get icons() {
        return Object.keys(WaypointIconSet)
    }
    get tracksOrdered() {
        return _.sortBy(this.tracks, t => t.name)
    }
    get tracksOrderedNonDegenerate() {
        const retval = _.sortBy(this.tracks.filter(t => !t.isDegenerate()), t => t.name);

        // use to test what happens when there's lots of tracks (scrolling, etc.)
        // for(let i = 0; i < 20; i++) {
        //     const newTrack = new TrackEntity(this.currentRenderer);
        //     newTrack.name = `ExtraTrack${i}`
        //     retval.push(newTrack)
        // }
        // for(let i = 0; i < 10; i++) {
        //     const newTrack = new TrackEntity(this.currentRenderer);
        //     newTrack.name = `ExtraRoute${i}`
        //     newTrack.type = 'route';
        //     retval.push(newTrack)
        // }
        return retval;
    }
    get waypointsOrdered() {
        return this.waypoints.dotsFeatureSource.getFeatures()
    }
    get leadInUpgradeLabel() {
        return 'Subscribe'; //store.getters.hasUserAlreadyUsedUpFreeTrial ? 'Upgrade' : 'Free trial'
    }
    get undoSuffix() {
        const topCommand = _.last(this.undoCommands)
        return topCommand ? ' ' + topCommand.name : ''
    }
    get showResave() {
        const retval = this.loadedTrackGpxUrl !== null 
            && this.loadedTrackIsPublic !== null 
            && this.loadedTrackOwnedByCurrentUser === true
            && this.$store.getters.isUserLoggedIn
        return retval
    }
    get proximityAlarmTooltip() {
        if(this.selectedWaypoint === null) {
            return null
        }
        if(this.selectedWaypoint.props.proximityAlarm === null) {
            return "Click to set proximity alarm"
        }
        const proximityAlarmOption = this.proximityAlarmOptions.find(o => o.value === this.selectedWaypointProximityAlarm)
        const proximityAlarmText = proximityAlarmOption ? proximityAlarmOption.text : `${this.selectedWaypoint.props.proximityAlarm.toFixed(0)}m`
        return `Proximity alarm = ${proximityAlarmText}. Click to change.`
    }
    get selectedWaypointProximityAlarm(): number|null {
        return this.selectedWaypoint?.props.proximityAlarm ?? null
    }
    set selectedWaypointProximityAlarm(value: number|null) {
        if(this.selectedWaypoint === null) {
            throw new Error("Tried to set selectedWaypointProximityAlarm when selectedWaypoint is null")
        }
        this.selectedWaypoint.props.proximityAlarm = value
        this.showWaypointProximityAlarmPicker = false
    }
    get proximityAlarmIcon() {
        return this.selectedWaypointProximityAlarm === null ? 'mdi-alarm-note-off' : 'mdi-alarm-note'
    }

    get showProgressMessage() {
        return this.progressMessage !== null
    }
    set showProgressMessage(newVal: boolean) {
        if(!newVal) {
            this.progressMessage = null
        }
    }
    get cloudGpxSortDirections() {
        return CloudGpxSortDirections
    }

    get homeIsShowingPricingSidebar(): boolean {
        return this.$store.getters.showingPricingSidebar;
    }
    set homeIsShowingPricingSidebar(newVal: boolean) {
        this.$store.commit('showingPricingSidebar', newVal)
    }
}